import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useTheme } from "@mui/material";
import muiSytle from "../../../style/components-style/muiStyle";
import { ORG_PALETTE, getOrgTokens } from "../../../theme/style/organizationPalette";
import "../../../style/pages-style/buildings/BuildingWorkspace.css";
import { BUILDING_SECTIONS } from "./buildingSections";

const BuildingSectionsAccordion = ({ building, selectedSection, onSectionSelect, allProcesses = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  // Aligned precisely to ASTM Uniformat II (Level 1 Categories -> Level 2 Subcategories)
  const buildingSections = BUILDING_SECTIONS;

  const handleCategoryClick = (category) => {
    if (onSectionSelect) {
      if (selectedSection?.categoryId === category.id && !selectedSection?.subcategoryId) {
        onSectionSelect(null);
      } else {
        onSectionSelect({
          categoryId: category.id,
          categoryName: category.name,
          subcategoryId: null,
          subcategoryName: null,
        });
      }
    }
  };

  const handleSubcategoryClick = (category, subcategory) => {
    if (onSectionSelect) {
      if (selectedSection?.categoryId === category.id && selectedSection?.subcategoryId === subcategory.id) {
        onSectionSelect(null);
      } else {
        onSectionSelect({
          categoryId: category.id,
          categoryName: category.name,
          subcategoryId: subcategory.id,
          subcategoryName: subcategory.name,
        });
      }
    }
  };

  const handleOperationalCarbonClick = () => {
    if (onSectionSelect) {
      if (selectedSection?.subcategoryId === "operational_carbon") {
        onSectionSelect(null);
      } else {
        onSectionSelect({
          categoryId: "whole_building",
          categoryName: "Operational Carbon",
          subcategoryId: "operational_carbon",
          subcategoryName: "Operational Carbon",
        });
      }
    }
  };

  const isSelected = (categoryId, subcategoryId) => {
    return selectedSection?.categoryId === categoryId && selectedSection?.subcategoryId === subcategoryId;
  };

  const hasData = (categoryId, subcategoryId) => {
    return allProcesses.some(
      (p) =>
        String(p.building_section_category || "").toLowerCase() === String(categoryId || "").toLowerCase() &&
        String(p.building_section_subcategory || "").toLowerCase() === String(subcategoryId || "").toLowerCase()
    );
  };

  const isOpCarbonSelected = selectedSection?.subcategoryId === "operational_carbon";
  const hasOpCarbonData = allProcesses.some((p) => p.building_section_subcategory === "operational_carbon");

  // Building List Section Colors
  const SELECTED_COLOR = "#10be50ff"; //  Dark Green
  const HAS_DATA_COLOR = "#166534"; // Existing Green
  const TRANSITION_BEZIER = "cubic-bezier(0.34, 1.56, 0.64, 1)";

  return (
    <Accordion
      sx={{
        ...muiSytle.getAccordion(isDark),
        margin: "0 !important",
        my: "0 !important",
        "&.Mui-expanded": {
          margin: "0 !important",
        },
        "&:before": {
          display: "none",
        },
        borderRadius: `${ORG_PALETTE.radius.card} !important`,
        background: isDark ? "#18181b" : "#ffffff",
        backdropFilter: "blur(12px)",
        border: `1px solid ${tok.border.card}`,
        boxShadow: tok.shadow.card,
        overflow: "hidden",
      }}
      elevation={0}
      defaultExpanded>
      <AccordionSummary sx={muiSytle.accordionSummary}>
        <Typography sx={muiSytle.getAccordionTitle(isDark)}>Building Sections</Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ px: ORG_PALETTE.spacing.tablePadH, py: ORG_PALETTE.spacing.tablePadV, pt: 1.5 }}>
        <Box sx={{ display: "flex", gap: ORG_PALETTE.spacing.sectionGap, alignItems: "stretch" }}>
          {/* Left Side: Standard Sections */}
          <Box className="building-sections-list" sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: ORG_PALETTE.spacing.sectionGap }}>
            {buildingSections.map((category) => {
              const isCatSelected = selectedSection?.categoryId === category.id && !selectedSection?.subcategoryId;
              const isCatIndeterminate = selectedSection?.categoryId === category.id && Boolean(selectedSection?.subcategoryId);

              return (
                <Box key={category.id} className="category-row" sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  {/* Left side: Category Name with Checkbox */}
                  <Box
                    onClick={() => handleCategoryClick(category)}
                    sx={{
                      width: "270px",
                      minWidth: "270px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      cursor: "pointer",
                      userSelect: "none",
                      borderRadius: "8px",
                      py: 0.5,
                      px: 0.75,
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                      },
                    }}
                  >
                    {/* Category Checkbox */}
                    <Box
                      sx={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "4px",
                        flexShrink: 0,
                        border: "2px solid",
                        borderColor: isCatSelected
                          ? SELECTED_COLOR
                          : isCatIndeterminate
                          ? (isDark ? "#4ade80" : "#16a34a")
                          : (isDark ? "#52525b" : "#cbd5e1"),
                        backgroundColor: isCatSelected
                          ? SELECTED_COLOR
                          : isCatIndeterminate
                          ? (isDark ? "rgba(34, 197, 94, 0.2)" : "rgba(22, 163, 74, 0.15)")
                          : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {isCatSelected && (
                        <Box
                          component="svg"
                          viewBox="0 0 12 10"
                          sx={{ width: "9px", height: "9px" }}
                        >
                          <path
                            d="M1 5l3.5 3.5L11 1"
                            stroke="white"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </Box>
                      )}
                      {isCatIndeterminate && !isCatSelected && (
                        <Box
                          sx={{
                            width: "8px",
                            height: "2px",
                            backgroundColor: isDark ? "#4ade80" : "#16a34a",
                            borderRadius: "1px",
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      sx={{
                        color: isCatSelected ? (isDark ? "#4ade80" : "#15803d") : (isDark ? "#e4e4e7" : "#444"),
                        fontWeight: isCatSelected || isCatIndeterminate ? 700 : 600,
                        fontSize: "14px",
                        fontFamily: "Poppins",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {category.name}
                    </Typography>

                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: "999px",
                        border: "1px solid",
                        borderColor: isCatSelected
                          ? SELECTED_COLOR
                          : isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)",
                        backgroundColor: isCatSelected
                          ? (isDark ? "rgba(34, 197, 94, 0.2)" : "#dcfce7")
                          : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.03)",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: isCatSelected ? (isDark ? "#4ade80" : "#15803d") : (isDark ? "#cbd5e1" : "#475569"),
                        }}
                      >
                        {category.code}
                      </Typography>
                    </Box>
                  </Box>

                {/* Right side: Subcategories Grid aligned to ASTM Level 2 */}
                <Box className="subcategory-grid" sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                  {category.subcategories.map((sub) => {
                    const selected = isSelected(category.id, sub.id);
                    const dataPresent = hasData(category.id, sub.id);

                    return (
                      <Box
                        key={sub.id}
                        onClick={() => handleSubcategoryClick(category, sub)}
                        sx={{
                          position: "relative",
                          padding: "6px 12px 6px 10px",
                          borderRadius: "10px",
                          cursor: "pointer",
                          transition: `all 0.4s ${TRANSITION_BEZIER}`,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          background: selected
                            ? `linear-gradient(135deg, ${SELECTED_COLOR}, #16a34a)`
                            : dataPresent
                            ? isDark
                              ? "rgba(22, 163, 74, 0.28)"
                              : "#d1fae5"
                            : isDark
                            ? "#27272a"
                            : "white",
                          border: "1.5px solid",
                          borderColor: selected
                            ? SELECTED_COLOR
                            : dataPresent
                            ? isDark ? "#22c55e" : "#86efac"
                            : isDark ? "#3f3f46" : "rgba(0,0,0,0.08)",
                          boxShadow: selected
                            ? "0 8px 16px -4px rgba(21, 128, 61, 0.4)"
                            : dataPresent
                            ? isDark ? "0 2px 8px rgba(34,197,94,0.15)" : "0 2px 8px rgba(22,163,74,0.12)"
                            : "0 1px 3px rgba(0,0,0,0.04)",
                          "&:hover": {
                            transform: "translateY(-2px) scale(1.02)",
                            boxShadow: selected
                              ? "0 12px 20px -6px rgba(21, 128, 61, 0.5)"
                              : dataPresent
                              ? "0 6px 16px rgba(22,163,74,0.2)"
                              : "0 6px 14px rgba(0,0,0,0.07)",
                            borderColor: selected ? "#14532d" : dataPresent ? (isDark ? "#4ade80" : "#4ade80") : isDark ? "#52525b" : "#ccc",
                          },
                        }}
                      >
                        {/* Square checkbox indicator */}
                        <Box
                          sx={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "4px",
                            flexShrink: 0,
                            border: "2px solid",
                            borderColor: selected
                              ? "rgba(255,255,255,0.95)"
                              : dataPresent
                              ? isDark ? "#4ade80" : "#16a34a"
                              : isDark ? "#52525b" : "#d1d5db",
                            backgroundColor: selected
                              ? "rgba(255,255,255,0.95)"
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {selected && (
                            <Box
                              component="svg"
                              viewBox="0 0 12 10"
                              sx={{ width: "9px", height: "9px" }}
                            >
                              <path
                                d="M1 5l3.5 3.5L11 1"
                                stroke="#16a34a"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "13px",
                              fontWeight: selected ? 600 : dataPresent ? 600 : 500,
                              color: selected ? "white" : dataPresent ? (isDark ? "#bbf7d0" : "#14532d") : isDark ? "#e4e4e7" : "#555",
                              letterSpacing: "0.01em",
                            }}
                          >
                            {sub.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "10px",
                              fontWeight: 700,
                              letterSpacing: "0.05em",
                              color: selected ? "rgba(255,255,255,0.9)" : dataPresent ? (isDark ? "#dcfce7" : "#15803d") : (isDark ? "#a1a1aa" : "#64748b"),
                            }}
                          >
                            {sub.code}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Vertical Divider */}
          <Box
            sx={{
              width: "1px",
              background: isDark ? "linear-gradient(to bottom, transparent, #3f3f46, transparent)" : "linear-gradient(to bottom, transparent, #e0e0e0, transparent)",
            }}
          />

          {/* Right Side: Operational Carbon */}
          <Box sx={{ width: "224px", flexShrink: 0, display: "flex", flexDirection: "column", gap: 1.2, justifyContent: "center" }}>
            <Box sx={{ textAlign: "center", marginBottom: 1 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, fontFamily: "Poppins", color: isDark ? "#94a3b8" : "#888", letterSpacing: "0.05em" }}>
                Operational Carbon Context
              </Typography>
            </Box>
            <Box
              onClick={handleOperationalCarbonClick}
              sx={{
                position: "relative",
                padding: "20px",
                borderRadius: "12px",
                cursor: "pointer",
                textAlign: "center",
                border: "1px solid",
                transition: `all 0.5s ${TRANSITION_BEZIER}`,
                background: isOpCarbonSelected
                  ? `linear-gradient(135deg, ${SELECTED_COLOR}, #16a34a)`
                  : hasOpCarbonData
                  ? isDark
                    ? "linear-gradient(135deg, rgba(22, 163, 74, 0.2), rgba(22, 163, 74, 0.1))"
                    : "linear-gradient(135deg, #f0fdf4, #dcfce7)"
                  : isDark
                  ? "linear-gradient(135deg, #27272a, #1c1c1e)"
                  : "linear-gradient(135deg, #ffffff, #f9fafb)",
                borderColor: isOpCarbonSelected ? SELECTED_COLOR : hasOpCarbonData ? HAS_DATA_COLOR : isDark ? "#3f3f46" : "rgba(0,0,0,0.08)",
                boxShadow: isOpCarbonSelected ? "0 12px 24px -6px rgba(21, 128, 61, 0.4)" : "0 4px 12px -2px rgba(0,0,0,0.05)",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.02)",
                  boxShadow: "0 20px 30px -10px rgba(0,0,0,0.1)",
                },
                overflow: "hidden",
              }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: isDark
                    ? "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.1) 0%, transparent 70%)"
                    : "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.4) 0%, transparent 70%)",
                  pointerEvents: "none",
                }}
              />

              <Typography
                sx={{
                  fontWeight: 600,
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  color: isOpCarbonSelected ? "white" : hasOpCarbonData ? "#15803d" : isDark ? "#e4e4e7" : "#333",
                }}>
                Operational Carbon
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontFamily: "Poppins",
                  opacity: isOpCarbonSelected ? 0.9 : 0.6,
                  marginTop: "6px",
                  color: isOpCarbonSelected ? "white" : hasOpCarbonData ? "#15803d" : isDark ? "#94a3b8" : "#666",
                }}>
                Includes B6 & B7 Stages
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default BuildingSectionsAccordion;
